/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import msdyn365Commerce from '@msdyn365-commerce/core';
import { Module, Node, Waiting } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import * as MsDyn365 from '@msdyn365-commerce/core';
import { PriceComponent } from '@msdyn365-commerce/components';

import { ICheckoutViewProps, ILineItem, ILineItemDeliveryGroup, ILineItems, IPickUpAtStore } from './citta-checkout-gwp';
import { ICittaCheckoutGwpProps } from './citta-checkout-gwp.props.autogenerated';

import { IEmailDelivery } from './components';

export const PickUpAtStoreComponent: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const EmailDeliveryComponent: React.FC<IEmailDelivery> = ({ EmailDelivery, label }) => <Node {...EmailDelivery}>{label}</Node>;

export const LineItemComponent: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => (
    <Node {...LineItem}>
        {item}
        {pickUpAtStore && <PickUpAtStoreComponent {...pickUpAtStore} />}
        {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
    </Node>
);

export const LineItemGroupComponent: React.FC<ILineItemDeliveryGroup> = ({ LineItemDeliveryGroup, LineItemList, heading, lineItems }) => (
    <Node {...LineItemDeliveryGroup}>
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponent key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemGroupComponentWithMultiplePickUp: React.FC<ILineItemDeliveryGroup> = ({
    LineItemDeliveryGroup,
    LineItemList,
    heading,
    lineItems,
    lineItemWraper,
    lineItemWraperIcon
}) => (
    <Node {...LineItemDeliveryGroup}>
        {lineItemWraperIcon}
        {lineItemWraper}
        {heading}
        <Node {...LineItemList}>
            {lineItems.map(lineItem => (
                <LineItemComponentWithMultiplePickUp key={lineItem.LineId} {...lineItem} />
            ))}
        </Node>
    </Node>
);

export const LineItemComponentWithMultiplePickUp: React.FC<ILineItem> = ({ LineItem, item, pickUpAtStore, emailDelivery }) => (
    <Node {...LineItem}>
        {item}
        {emailDelivery && <EmailDeliveryComponent {...emailDelivery} />}
    </Node>
);

export const PickUpAtStoreComponentWithMultiplePickUp: React.FC<IPickUpAtStore> = ({ PickUpAtStore, label, location }) => (
    <Node {...PickUpAtStore}>
        {label}
        {location}
    </Node>
);

export const LineItemsComponent: React.FC<ILineItems> = ({
    LineItems,
    Header,
    heading,
    editLink,
    itemsForPickup,
    itemsForShip,
    itemsForEmail,
    itemsGroupWithMulitplePickupMode
}) => (
    <Node {...LineItems}>
        <Node {...Header}>
            {heading}
            {editLink}
        </Node>
        {itemsGroupWithMulitplePickupMode === undefined && itemsForPickup && <LineItemGroupComponent {...itemsForPickup} />}
        {itemsGroupWithMulitplePickupMode === undefined && itemsForEmail && <LineItemGroupComponent {...itemsForEmail} />}
        {itemsGroupWithMulitplePickupMode === undefined && itemsForShip && <LineItemGroupComponent {...itemsForShip} />}
        {itemsGroupWithMulitplePickupMode !== undefined
            ? itemsGroupWithMulitplePickupMode.map((item, index) => {
                  return <LineItemGroupComponentWithMultiplePickUp {...item} key={index} />;
              })
            : null}
    </Node>
);

const _renderOrderSummarylines = (props: ICheckoutViewProps & ICittaCheckoutGwpProps<{}>): JSX.Element | null => {
    const isB2B = props.context?.request?.app?.platform?.enableDefaultOrderQuantityLimits === 'b2b';
    const {orderSummary } = props;
    if (!orderSummary) {
        return null;
    }
    return (
        <div className='msc-order-summary-wrapper'>
        {orderSummary.heading}
        <div className='msc-order-summary__items'>
            {orderSummary.lines && (
                <>
                    {isB2B ? orderSummary.lines.subtotal : _renderSubTotal(props)}
                    {isB2B ? null : orderSummary.lines.totalDiscounts}
                    {_renderShippingInclTaxes(props)}
                    {_renderOtherCharges(props)}
                    {/*hide on us*/}
                    {props.config.hideTaxLine ? null : orderSummary.lines.tax}
                    {_renderTotalOrder(props)}
                    {orderSummary.lines.loyalty}
                    {orderSummary.lines.giftCard}
                    {isB2B ? null : orderSummary.lines.orderTotal}
                    {isB2B? null : _renderTotalSavings(props)}
                </>
            )}
        </div>
        {props.config.giftWithPurchase ? _renderGiftWithPurchasMessage(props) : _renderFreeShippingMessage(props)}
        {_getCheckoutSummaryText(props)}
    </div>
    );
};

function _renderOtherCharges(props: ICheckoutViewProps & ICittaCheckoutGwpProps<{}>): React.ReactNode | null{
    const chargeAmountInclTax = props.data.checkout.result?.checkoutCart.cart.ChargeAmount && props.data.checkout.result?.checkoutCart.cart.ChargeAmount || 0;
    const shippingCharges = props.data.checkout.result?.checkoutCart.cart.ShippingChargeAmount && props.data.checkout.result?.checkoutCart.cart.ShippingChargeAmount || 0;
    const chargeLines = props.data.checkout.result?.checkoutCart.cart.ChargeLines;

    let otherChargesAmountFromLines = 0;
    let totalChargeAmountFromLines = 0;
    let chargesOverriden = false;
    let displayOtherChargesAmount = 0;
    let shippingChargesFromLines = 0;
    const freightChargeCode = props.context.app.config.freightChargeCode;

    if(chargeLines) {
        chargeLines.forEach(chargeLine => {
            const chargeAmountInclTaxLocal = chargeLine.CalculatedAmount ? chargeLine.CalculatedAmount : 0;
            if (chargeLine.ChargeCode != freightChargeCode) {
                otherChargesAmountFromLines += chargeAmountInclTaxLocal;
            }
            if (chargeLine.ChargeCode == freightChargeCode){
                shippingChargesFromLines += chargeAmountInclTaxLocal;
            }
        });
    }

    if(shippingChargesFromLines == 0 &&  shippingCharges != shippingChargesFromLines)
    {
        totalChargeAmountFromLines = shippingCharges + otherChargesAmountFromLines;
    }
    else{
        totalChargeAmountFromLines = shippingChargesFromLines + otherChargesAmountFromLines;
    }
    
    chargesOverriden = totalChargeAmountFromLines != chargeAmountInclTax;
    displayOtherChargesAmount = chargesOverriden ? 0 : otherChargesAmountFromLines;
    if (displayOtherChargesAmount == 0) {
        return null;
    }

    return (
        <p className={`msc-order-summary__line_other-charges`}>
           {displayOtherChargesAmount && displayOtherChargesAmount > 0 ? (
            <>
                    <span className='msc-order-summary__label'>Other charges</span>
                    <PriceComponent
                        data={{
                            price: { CustomerContextualPrice: displayOtherChargesAmount }
                        }}
                        freePriceText={undefined}
                        context={props.context}
                        id={props.id}
                        typeName={props.typeName}
                        className={'msc-order-summary__value'}
                    />
                </>
            ) : null}
        </p>
    );
}

function _renderShippingInclTaxes(props: ICheckoutViewProps & ICittaCheckoutGwpProps<{}>): React.ReactNode | null{
    const chargeLines = props.data.checkout.result?.checkoutCart.cart.ChargeLines;
    let chargeAmountInclTax = 0;
    if(chargeLines) {
        chargeLines.forEach(chargeLine => {
            if (chargeLine.IsShipping) {
                const chargeAmountInclTaxLocal = chargeLine.GrossAmount ? chargeLine.GrossAmount : 0;
                chargeAmountInclTax += chargeAmountInclTaxLocal;
            }
        });
    }
    if (chargeAmountInclTax == 0) {
        return props.orderSummary?.lines?.shipping;
    }

    return (
        <p className={`msc-order-summary__line-shipment`}>
           {chargeAmountInclTax && chargeAmountInclTax > 0 ? (
            <>
                    <span className='msc-order-summary__label'>{props.resources.shippingLabel}</span>
                    <PriceComponent
                        data={{
                            price: { CustomerContextualPrice: chargeAmountInclTax }
                        }}
                        freePriceText={undefined}
                        context={props.context}
                        id={props.id}
                        typeName={props.typeName}
                        className={'msc-order-summary__value'}
                    />
                </>
            ) : null}
        </p>
    );
}


function _renderTotalSavings(props: ICheckoutViewProps): JSX.Element | null {
    const DiscountAmount = props.data.checkout.result?.checkoutCart.cart.DiscountAmount;
    return (
        <p className={`msc-order-summary__line--discount-amount`}>
            {DiscountAmount && DiscountAmount > 0 ? (
            <>
                    <span className='msc-order-summary__label'>{props.resources.totalSavingsLabel}</span>
                    <PriceComponent
                        data={{
                            price: { CustomerContextualPrice: DiscountAmount }
                        }}
                        freePriceText={undefined}
                        context={props.context}
                        id={props.id}
                        typeName={props.typeName}
                        className={'msc-order-summary__value'}
                    />
                </>
            ) : null}
        </p>
    );
}

function _renderSubTotal(props: ICheckoutViewProps): JSX.Element | null {
    var subtotal=0;
    props.data.checkout.result?.checkoutCart.cart.CartLines?.forEach((item)=>{
     subtotal += item.Price! * item.Quantity!;
    });
     const OrderSubTotal = subtotal;//props.data.checkout.result?.checkoutCart.cart.NetPrice;
     return (
         <p className={`msc-order-summary__line-sub-total`}>
            {OrderSubTotal && OrderSubTotal > 0 ? (
             <>
                     <span className='msc-order-summary__label'>{props.resources.subTotalLabel}</span>
                     <PriceComponent
                         data={{
                             price: { CustomerContextualPrice: OrderSubTotal }
                         }}
                         freePriceText={undefined}
                         context={props.context}
                         id={props.id}
                         typeName={props.typeName}
                         className={'msc-order-summary__value'}
                     />
                 </>
             ) : null}
         </p>
     );
}

function _renderTotalOrder(props: ICheckoutViewProps): JSX.Element | null {
    const OrderTotal = props.data.checkout.result?.checkoutCart.cart.TotalAmount;
    return (
        <p className={`msc-order-summary__line-order-total`}>
           {OrderTotal && OrderTotal > 0 ? (
            <>
                    <span className='msc-order-summary__label'>{props.resources.totalAmountLabel}</span>
                    <PriceComponent
                        data={{
                            price: { CustomerContextualPrice: OrderTotal }
                        }}
                        freePriceText={undefined}
                        context={props.context}
                        id={props.id}
                        typeName={props.typeName}
                        className={'msc-order-summary__value'}
                    />
                </>
            ) : null}
        </p>
    );
}

function _renderProgressBar(percentFilled: number): JSX.Element | null {
    return <>
        <div className="citta-progress-bar">
            <div className="citta-progress-bar__progress" style={{width:`${percentFilled}%`}}></div>
        </div>
    </>
}

function _renderGiftWithPurchasMessage(props: ICheckoutViewProps & ICittaCheckoutGwpProps<{}>): JSX.Element | null {

    const shippingAmount = props.data.checkout.result?.checkoutCart.cart.DeliveryModeChargeAmount || 0;
    const OrderSubTotal = (props.data.checkout.result?.checkoutCart.cart.AmountDue || 0) - shippingAmount;
    let nonSaleSubtotal = 0;
    let NonSaleItemsCartLines = props.data.checkout.result?.checkoutCart.cart.CartLines?.filter((line) => line.DiscountAmount === 0);
    NonSaleItemsCartLines && NonSaleItemsCartLines.forEach((item) => {
        nonSaleSubtotal += item.Price! * item.Quantity!;
    })
    if (!props.config.giftWithPurchase || !OrderSubTotal || props.data.checkout.result?.checkoutCart.cart.CartLines?.some(line => line.ItemId === "GiftCard")) {
        return _renderFreeShippingMessage(props);
    }
    let progressBar:JSX.Element | null = null;

    switch(true){
        case nonSaleSubtotal < 200:
        progressBar = <> 
        {_renderProgressBar(nonSaleSubtotal / 2)}
        <div className="shipping-to-free-message">
        Spend an additional ${Math.ceil( 200  - nonSaleSubtotal)} to be eligible for a free Mae hand towel (worth $39.90). Enter promo code <strong>GIFTSPRING</strong>.
        </div>  
        </> 
        break;

        case nonSaleSubtotal >=200 && OrderSubTotal >=200 && OrderSubTotal < 400:
            progressBar = <>
            {_renderProgressBar(OrderSubTotal/4)}
            <div className="shipping-to-free-message">
            Spend an additional ${Math.ceil(400 - OrderSubTotal)} to be eligible for a free Cabin beach towel (worth $109). Enter promo code <strong>GIFTSPRING</strong>.
            </div>
            </>
        break;

        case nonSaleSubtotal >=200 && OrderSubTotal>=400 && OrderSubTotal < 1000:
            progressBar = <>
            {_renderProgressBar(OrderSubTotal/10)}
            <div className="shipping-to-free-message">
            Spend an additional ${Math.ceil(1000 - OrderSubTotal)} to be eligible for a free Rothko throw (worth $309). Enter promo code <strong>GIFTSPRING</strong>.
            </div>
            </>

        break;

        case nonSaleSubtotal >=200 && OrderSubTotal>= 1000:
            progressBar = <>
            {_renderProgressBar(100)}
            <div className="shipping-to-free-message">
            You're eligible for a free Rothko throw (worth $309). Enter promo code <strong>GIFTSPRING</strong>.
            </div>
            </>
        break;
        default: null
    }

    return progressBar;
   
}


function _renderFreeShippingMessage(props: ICheckoutViewProps & ICittaCheckoutGwpProps<{}>): JSX.Element | null {
    const shippingAmount = props.data.checkout.result?.checkoutCart.cart.DeliveryModeChargeAmount || 0;
    const OrderSubTotal = (props.data.checkout.result?.checkoutCart.cart.AmountDue || 0) - shippingAmount;
        
    const freeShippingAmount = props.config.freeShippingAmount;
    if (!freeShippingAmount || !OrderSubTotal || OrderSubTotal >= freeShippingAmount) {
        return null;
    }
    return <div className="shipping-to-free-message">
        Spend an additional ${Math.ceil(freeShippingAmount - OrderSubTotal)} to get <b>FREE SHIPPING</b>
    </div>
}

const _getCheckoutSummaryText = (props: ICheckoutViewProps & ICittaCheckoutGwpProps<{}>): React.ReactNode | undefined => {
    const { paragraph } = props.config;

    return (
        paragraph && (
            <MsDyn365.RichTextComponent
                text={paragraph}
                className='msc-order-summary__summary-text'
            />
        )
    );
};

const CheckoutView: React.FC<ICheckoutViewProps> = props => {
    const {
        isPaymentVerificationRedirection,
        shouldEnableSinglePaymentAuthorizationCheckout,
        canShow,
        checkoutProps,
        headerProps,
        hasSalesOrder,
        bodyProps,
        mainProps,
        mainControlProps,
        sideProps,
        sideControlFirstProps,
        sideControlSecondProps,
        termsAndConditionsProps,
        orderConfirmation,
        loading,
        alert,
        title,
        guidedForm,
        lineItems,
        placeOrderButton,
        termsAndConditions,
        keepShoppingButton,
        checkoutExpressPaymentContainer,
        checkoutErrorRef
    } = props;


    if (!props.data.checkout || !props.data.checkout.result) {
        return null;
    }

    let isBusy = false;
    if(msdyn365Commerce.isBrowser) {
        const url: string = decodeURI(window.location.search);
        const urlParams: URLSearchParams = new URLSearchParams(url);
        if (urlParams.get('paymentMethod') === 'AP' && urlParams.has('orderToken')) {
            const orderToken = urlParams.get('orderToken');
            const afterpayStatus = urlParams.get('status') || 'FAILURE';

            if (afterpayStatus === 'SUCCESS' && orderToken) {
                isBusy = true;
            }
        }
    }
    
    return (
        <Module {...checkoutProps} ref={checkoutErrorRef}>
            {!hasSalesOrder && !checkoutExpressPaymentContainer && <Node {...headerProps}>{title}</Node>}
            {!hasSalesOrder && isPaymentVerificationRedirection && shouldEnableSinglePaymentAuthorizationCheckout && !alert && !loading && (
                <Waiting className='msc-waiting-circular msc-waiting-lg' />
            )}
            {!hasSalesOrder && (
                <Node {...bodyProps}>
                    {loading}
                    {alert}
                    {
                        isBusy ? (
                            <div className="afterpay-loading-wrapper">
                                <div className='afterpay-loading-wrapper-spinner' />   
                            </div> 
                        ) : null
                    }
                    {canShow && (
                        <>
                            <Node {...mainProps}>
                                {checkoutExpressPaymentContainer}
                                {checkoutExpressPaymentContainer && <Node {...headerProps}>{title}</Node>}
                                {guidedForm}
                                <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                <Node {...mainControlProps}>
                                    {placeOrderButton}
                                    {keepShoppingButton}
                                </Node>
                            </Node>
                            <Node {...sideProps}>
                                {_renderOrderSummarylines(props)}
                                <Node {...sideControlFirstProps}>
                                    <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                    {placeOrderButton}
                                    {keepShoppingButton}
                                </Node>
                                {lineItems && <LineItemsComponent {...lineItems} />}
                                <Node {...sideControlSecondProps}>
                                    <Node {...termsAndConditionsProps}>{termsAndConditions}</Node>
                                    {placeOrderButton}
                                    {keepShoppingButton}
                                </Node>
                            </Node>
                        </>
                    )}
                </Node>
            )}
            {hasSalesOrder && orderConfirmation}
        </Module>
    );
};

export default CheckoutView;
